import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs } from "../../api/examApi";
import { listStudents } from "../../api/studentApi";
import { cleanText } from "../../utils/textUtils";
import "../Attendance/Attendance.css";
import "./AdmitCard.css";

const academicYearApi = createLookupApi("academic-years");

/**
 * Inline equivalent of digicampusbd's `seat-plan-download` ("Exam Seat
 * Label") — same underlying student roster as `AdmitCardPage`
 * (`AdmitSeatPlanService.downloadStudentSeatPlan` calls the exact same
 * `provideStudentIdentifications`/`copyIdentificationToAdmitSeatPlanResponse`
 * pair as the admit card, confirmed by reading the service directly), just
 * printed as a seat-label roster instead of one card per student. The old
 * page's "Select Template" dropdown (Seat Plan 1/2) picks between two
 * cosmetic Jasper PDF layouts of this same data — no functional difference
 * (same fields, same students) — so, per the same reasoning already applied
 * to `exam-marksheet-template`'s decorative layout picker, only one layout
 * is built here rather than duplicating the same table twice.
 */
export default function SeatPlanPage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [fromRoll, setFromRoll] = useState("");
  const [toRoll, setToRoll] = useState("");

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    if (!classConfigurationId || !academicYear) {
      setStudents([]);
      return;
    }
    setLoading(true);
    setError("");
    let cancelled = false;
    listStudents(academicYear, classConfigurationId)
      .then((data) => {
        if (!cancelled) setStudents(data);
      })
      .catch(() => setError("Failed to load students."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [classConfigurationId, academicYear]);

  const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
  const examName = examConfigs.find((ec) => String(ec.examConfigId) === examConfigId)?.examName || "";

  const rows = useMemo(() => {
    const from = fromRoll ? Number(fromRoll) : 0;
    const to = toRoll ? Number(toRoll) : 0;
    return students
      .filter((s) => (from === 0 || to === 0 ? true : s.studentRoll >= from && s.studentRoll <= to))
      .slice()
      .sort((a, b) => a.studentRoll - b.studentRoll);
  }, [students, fromRoll, toRoll]);

  return (
    <MainLayout>
      <div className="page-header no-print">
        <h1>Exam Seat Label</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters no-print">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section List
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam List
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        <label>
          From Roll
          <input type="number" value={fromRoll} onChange={(e) => setFromRoll(e.target.value)} />
        </label>
        <label>
          To Roll
          <input type="number" value={toRoll} onChange={(e) => setToRoll(e.target.value)} />
        </label>
        <button type="button" className="btn-secondary" onClick={() => window.print()} disabled={rows.length === 0}>
          Print
        </button>
      </div>

      <p className="no-print" style={{ fontStyle: "italic" }}>
        {cc ? cc.classConfigName : ""} {examName ? `— ${examName}` : ""}
      </p>

      {loading ? (
        <p>Loading...</p>
      ) : rows.length === 0 ? (
        <p>No students found for this selection.</p>
      ) : (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Roll</th>
                <th>Student ID</th>
                <th>Name</th>
                <th>Registration No</th>
                <th>Class</th>
                <th>Group</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.identificationId}>
                  <td>{s.studentRoll}</td>
                  <td>{s.customStudentId}</td>
                  <td>{s.studentName}</td>
                  <td>{cleanText(s.registrationNo, "-")}</td>
                  <td>{cc ? cc.classConfigName : ""}</td>
                  <td>{s.groupName && s.groupName !== "N/A" ? s.groupName : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </MainLayout>
  );
}
